// src/utils/analyticsExport.js
// Generates formatted CSV exports for analytics data

const toDate = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (typeof ts === 'number') return new Date(ts);
  return new Date(ts);
};

const fmtDate = (ts) => {
  const d = toDate(ts);
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtDateShort = (ts) => {
  const d = toDate(ts);
  if (!d || isNaN(d.getTime())) return '';
  return d.toISOString().split('T')[0];
};

const escapeCsv = (val) => {
  const str = String(val ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const buildCsvRow = (vals) => vals.map(escapeCsv).join(',');

// Build summary statistics from an array of events
const buildSummary = (events) => {
  const sessions = new Set(events.map(e => e.sessionId).filter(Boolean));
  const types = {};
  const pages = {};
  const sources = {};
  const sourceSessionsSeen = new Set();
  const ctaButtons = {};
  const sections = {};
  const scrollDepths = { 25: 0, 50: 0, 75: 0, 100: 0 };
  const dailyCounts = {};

  events.forEach(e => {
    // Type counts
    types[e.type] = (types[e.type] || 0) + 1;

    // Page views — normalise free-activity (new) and legacy lesson-without-userId
    if (e.type === 'page_view' && e.page) {
      const pg = (e.page === 'free-activity' || (e.page === 'lesson' && !e.userId)) ? 'free activity' : e.page;
      pages[pg] = (pages[pg] || 0) + 1;
    }

    // Referral sources (unique by session)
    const src = (e.referralSource || 'direct').toLowerCase();
    if (src && src !== 'localhost' && src !== '127.0.0.1') {
      const key = `${e.sessionId}_${src}`;
      if (!sourceSessionsSeen.has(key)) {
        sourceSessionsSeen.add(key);
        sources[src] = (sources[src] || 0) + 1;
      }
    }

    // CTA clicks
    if (e.type === 'cta_click' && e.metadata?.button) {
      ctaButtons[e.metadata.button] = (ctaButtons[e.metadata.button] || 0) + 1;
    }

    // Section views
    if (e.type === 'section_view' && e.metadata?.section) {
      sections[e.metadata.section] = (sections[e.metadata.section] || 0) + 1;
    }

    // Scroll depth
    if (e.type === 'scroll_depth' && e.metadata?.depth) {
      const d = e.metadata.depth;
      if (scrollDepths[d] !== undefined) scrollDepths[d]++;
    }

    // Daily breakdown
    const dateStr = fmtDateShort(e.timestamp);
    if (dateStr) {
      if (!dailyCounts[dateStr]) dailyCounts[dateStr] = { events: 0, sessions: new Set() };
      dailyCounts[dateStr].events++;
      if (e.sessionId) dailyCounts[dateStr].sessions.add(e.sessionId);
    }
  });

  return { sessions: sessions.size, types, pages, sources, ctaButtons, sections, scrollDepths, dailyCounts };
};

// Sort object entries descending by value
const sortDesc = (obj) => Object.entries(obj).sort((a, b) => b[1] - a[1]);

export const exportAnalyticsCsv = (events, filename = 'analytics-export') => {
  const summary = buildSummary(events);
  const lines = [];

  // ── SUMMARY SECTION ──
  lines.push('ANALYTICS REPORT');
  lines.push(buildCsvRow(['Generated', new Date().toLocaleString()]));
  lines.push(buildCsvRow(['Total Events', events.length]));
  lines.push(buildCsvRow(['Unique Sessions', summary.sessions]));

  const dates = events.map(e => toDate(e.timestamp)).filter(d => d && !isNaN(d.getTime())).sort((a, b) => a - b);
  if (dates.length > 0) {
    lines.push(buildCsvRow(['Date Range', fmtDate(dates[0]) + ' to ' + fmtDate(dates[dates.length - 1])]));
  }
  lines.push('');

  // ── EVENT TYPE BREAKDOWN ──
  lines.push('EVENT TYPE BREAKDOWN');
  lines.push(buildCsvRow(['Event Type', 'Count', '% of Total']));
  sortDesc(summary.types).forEach(([type, count]) => {
    lines.push(buildCsvRow([type.replace(/_/g, ' '), count, (count / events.length * 100).toFixed(1) + '%']));
  });
  lines.push('');

  // ── PAGE VIEWS ──
  if (Object.keys(summary.pages).length > 0) {
    lines.push('PAGE VIEWS');
    lines.push(buildCsvRow(['Page', 'Views', '% of Page Views']));
    const totalPV = Object.values(summary.pages).reduce((a, b) => a + b, 0);
    sortDesc(summary.pages).forEach(([page, count]) => {
      lines.push(buildCsvRow([page, count, (count / totalPV * 100).toFixed(1) + '%']));
    });
    lines.push('');
  }

  // ── TRAFFIC SOURCES ──
  if (Object.keys(summary.sources).length > 0) {
    lines.push('TRAFFIC SOURCES (by unique sessions)');
    lines.push(buildCsvRow(['Source', 'Sessions', '% of Sessions']));
    sortDesc(summary.sources).forEach(([source, count]) => {
      lines.push(buildCsvRow([source, count, (count / summary.sessions * 100).toFixed(1) + '%']));
    });
    lines.push('');
  }

  // ── CTA CLICKS ──
  if (Object.keys(summary.ctaButtons).length > 0) {
    lines.push('CTA CLICK BREAKDOWN');
    lines.push(buildCsvRow(['Button', 'Clicks']));
    sortDesc(summary.ctaButtons).forEach(([btn, count]) => {
      lines.push(buildCsvRow([btn.replace(/_/g, ' '), count]));
    });
    lines.push('');
  }

  // ── SECTION ENGAGEMENT ──
  if (Object.keys(summary.sections).length > 0) {
    const homeViews = summary.pages['home'] || 1;
    lines.push('SECTION ENGAGEMENT (Homepage)');
    lines.push(buildCsvRow(['Section', 'Views', '% of Home Visitors']));
    sortDesc(summary.sections).forEach(([section, count]) => {
      lines.push(buildCsvRow([section.replace(/-/g, ' '), count, (count / homeViews * 100).toFixed(1) + '%']));
    });
    lines.push('');
  }

  // ── SCROLL DEPTH ──
  const hasScroll = Object.values(summary.scrollDepths).some(v => v > 0);
  if (hasScroll) {
    lines.push('SCROLL DEPTH (Homepage)');
    lines.push(buildCsvRow(['Depth', 'Sessions']));
    Object.entries(summary.scrollDepths).forEach(([depth, count]) => {
      lines.push(buildCsvRow([depth + '%', count]));
    });
    lines.push('');
  }

  // ── CONVERSION FUNNEL ──
  const homeViews = summary.pages['home'] || 0;
  const freeActivityViews = summary.pages['free activity'] || 0;
  const totalLandings = homeViews + freeActivityViews;
  const loginViews = summary.pages['login'] || 0;
  const signups = summary.types['signup'] || 0;
  const freeTrialClicks = summary.ctaButtons['free_trial'] || 0;
  const ageSelectClicks = summary.ctaButtons['trial_age_select'] || 0;
  const startFreeClicks = summary.ctaButtons['start_free'] || 0;
  if (totalLandings > 0) {
    const funnelBase = totalLandings || 1;
    lines.push('CONVERSION FUNNEL');
    lines.push(buildCsvRow(['Step', 'Count', '% of Landing Views', 'Drop-off from Previous']));
    const steps = [
      ['Landing Page Views', totalLandings],
      ['Scrolled to Age Picker', summary.sections['age-picker'] || 0],
      ['Scrolled to Final CTA', summary.sections['final-cta'] || 0],
      ['Clicked Free Trial / Age Select', freeTrialClicks + ageSelectClicks],
      ['Clicked Start Free', startFreeClicks],
      ['Visited Login Page', loginViews],
      ['Signed Up', signups],
    ];
    steps.forEach(([label, val], i) => {
      const pctOfBase = (val / funnelBase * 100).toFixed(1) + '%';
      const prev = i > 0 && steps[i - 1][1] > 0 ? (val / steps[i - 1][1] * 100).toFixed(1) + '%' : '';
      lines.push(buildCsvRow([label, val, pctOfBase, prev]));
    });
    lines.push('');
  }

  // ── DAILY BREAKDOWN ──
  const sortedDays = Object.entries(summary.dailyCounts).sort((a, b) => a[0].localeCompare(b[0]));
  if (sortedDays.length > 0) {
    lines.push('DAILY BREAKDOWN');
    lines.push(buildCsvRow(['Date', 'Sessions', 'Events']));
    sortedDays.forEach(([date, data]) => {
      lines.push(buildCsvRow([date, data.sessions.size, data.events]));
    });
    lines.push('');
  }

  // ── RAW EVENTS ──
  lines.push('RAW EVENT LOG');
  lines.push(buildCsvRow(['Timestamp', 'Type', 'Page', 'Detail', 'User', 'Session ID', 'Referral Source', 'Referral Medium', 'Referral Campaign']));
  events.forEach(e => {
    const detail = e.type === 'scroll_depth' ? (e.metadata?.depth + '%')
      : e.type === 'cta_click' ? (e.metadata?.button || '')
      : e.type === 'section_view' ? (e.metadata?.section || '')
      : e.type === 'lesson_start' || e.type === 'lesson_complete' ? ('lesson ' + (e.metadata?.lessonId ?? ''))
      : '';
    const displayPage = (e.page === 'free-activity' || (e.page === 'lesson' && !e.userId)) ? 'free activity' : (e.page || '');
    lines.push(buildCsvRow([
      fmtDate(e.timestamp),
      e.type,
      displayPage,
      detail,
      e.userId || '',
      e.sessionId || '',
      e.referralSource || '',
      e.referralMedium || '',
      e.referralCampaign || '',
    ]));
  });

  // Download
  const csv = lines.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}-${fmtDateShort(new Date())}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};
